import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Autocomplete,
  AutocompleteOption,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Option,
  Radio,
  RadioGroup,
  Select,
  Sheet,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect } from "react";
import { ThreeColumnGridRow } from "../../../../components/stateless/layouts";
import { NumericFormat } from "react-number-format";
import { Toggler } from "../../../../components/utils";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { theme } from "../../../../components/Theme";
import { CustomCreateCatalogType, TaxIDType } from "../../../../types/catalog";
import { PaymentFlowTypeEnum, paymentFlowTypes } from "../../../../types/order";
import ChangeCurrencyModal from "../../../Inventory/components/ChangeCurrencyModal";
import { currencyList } from "../../../Order/EditOrders";
import { CatalogLayout } from "../../../../utils/constants";
import InfoIcon from "../../../../components/Icons/InfoIcon";
import { ListBulletIcon } from "@heroicons/react/24/solid";

type Props = {
  catalog: CustomCreateCatalogType;
  nationalTaxes: TaxIDType[];
};

const CatalogInfoSection = ({ catalog, nationalTaxes }: Props) => {
  const { t } = useTranslation();

  const {
    control,
    register,
    watch,
    setValue,
    getValues,
    resetField,
    formState: { errors },
  } = useFormContext();

  const [currencyModal, setCurrencyModal] = useState({
    show: false,
    currency: catalog.currency,
  });
  const [oldCurrency, setOldCurrency] = useState(getValues("currency"));

  // watched fields
  const selectedTax = nationalTaxes?.find((tax) => tax.id === watch("taxId"));
  // const oldCurrency = watch("currency");

  /**
   * get national taxes based on business's country
   */
  // async function getAndSetNationalTaxes() {
  //   try {
  //     const res = await TaxService.getTaxesOption(
  //       session?.activeBusiness.companyCountry as string
  //     );

  //     setNationalTaxes(res.map((res: any) => res));
  //     setValue(
  //       "taxId",
  //       nationalTaxes.find((t) => t.taxLabel === "NONE") || null
  //     );
  //   } catch (error) {
  //     console.error(error);
  //   }
  // }

  // useEffect(() => {
  //   getAndSetNationalTaxes();
  // }, [session?.activeBusiness.id]);

  useEffect(() => {
    if (catalog.catalogType === "PUBLIC") {
      setValue("paymentFlowType", PaymentFlowTypeEnum.FULL_BEFORE_DELIVERY);
    }
  }, [catalog.catalogType]);

  return (
    <Box
      sx={{
        borderWidth: { xs: 0, md: "1px" },
        borderColor: theme.palette.neutral[300],
        borderRadius: "8px",
        py: "32px",
        px: "24px",
      }}
    >
      <Typography level="text-xl-bold" sx={{ color: "#101828" }}>
        {t("catalog.createCatalog.catalogInformation")}
      </Typography>

      <Grid container spacing={2} mt={4}>
        <Grid xs={12} lg={6}>
          <FormControl sx={{ flex: "1" }} error={!!errors.catalogName}>
            <FormLabel>
              {t("catalog.createCatalog.catalogueName")}{" "}
              <span className="text-danger-400">*</span>
            </FormLabel>
            <Input
              type="text"
              placeholder={t("catalog.createCatalog.catalogueName")}
              aria-label="catalogName"
              {...register("catalogName", {
                required: t("commons.error.form.required", {
                  label: t("catalog.createCatalog.catalogueName"),
                }),
                minLength: {
                  value: 3,
                  message: t("commons.error.form.minLength", {
                    label: t("catalog.createCatalog.catalogueName"),
                    min: 3,
                  }),
                },
              })}
              error={!!errors.catalogName}
              className="min-h-[40px]"
            />
            <FormHelperText>
              {(errors.catalogName as any)?.message}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid container spacing={2} xs={12} lg={6}>
          <Grid
            xs={12}
            lg={
              getValues("paymentFlowType") ===
              PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
                ? 6
                : 12
            }
          >
            <Controller
              name="paymentFlowType"
              control={control}
              render={({ field: { onChange, onBlur, name, value, ref } }) => {
                return (
                  <FormControl sx={{ flex: "1" }} ref={ref}>
                    <FormLabel>Type of Payment *</FormLabel>
                    <Autocomplete
                      name={name}
                      id="create-catalog-payment-flow-type-autocomplete"
                      freeSolo={true}
                      placeholder={t("order.createOrder.form.paymentFlowType")}
                      options={paymentFlowTypes.filter(
                        (pt) =>
                          pt.value !== PaymentFlowTypeEnum.SPLIT_PAYMENT &&
                          pt.value !== PaymentFlowTypeEnum.RECURRING_PAYMENT
                      )}
                      disabled={catalog.catalogType == "PUBLIC"}
                      getOptionLabel={(option) =>
                        typeof option !== "string" ? option?.label : ""
                      }
                      onChange={(_event, newValue) => {
                        if (typeof newValue !== "string") {
                          if (
                            newValue?.value !==
                            PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
                          ) {
                            resetField("multiPaymentsData.paymentTimes");
                            resetField("multiPaymentsData.paymentHistory");
                          }
                          onChange(
                            newValue?.value as
                              | keyof typeof PaymentFlowTypeEnum
                              | null
                          );
                        }
                      }}
                      value={
                        paymentFlowTypes.find((pft) => pft.value === value) ||
                        null
                      }
                      onBlur={onBlur}
                      sx={{ width: { xs: "100%", lg: "100%" } }}
                      renderOption={(props, option) => (
                        <AutocompleteOption
                          {...props}
                          key={option.value}
                          sx={{
                            "&:hover": {
                              backgroundColor: "var(--joy-palette-neutral-100)",
                            },
                          }}
                        >
                          <Stack
                            direction={"row"}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            flex={1}
                          >
                            <Stack>
                              {option.label}
                              <Typography>{option.description}</Typography>
                            </Stack>
                          </Stack>
                        </AutocompleteOption>
                      )}
                    />
                  </FormControl>
                );
              }}
            />
            <FormHelperText>
              {(errors.paymentFlowType as any)?.message}
            </FormHelperText>
          </Grid>
          {getValues("paymentFlowType") ===
            PaymentFlowTypeEnum.MULTIPLE_PAYMENTS && (
            <Grid xs={12} lg={6}>
              <Controller
                name="multiPaymentsData.paymentTimes"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, ref } }) => (
                  <FormControl>
                    <FormLabel>
                      {t("order.multiPayment.paymentTimesLabel")}
                      <span className="text-danger-400">*</span>
                    </FormLabel>

                    <Select
                      ref={ref}
                      color={errors.paymentTimes ? "danger" : "neutral"}
                      indicator={<KeyboardArrowDown />}
                      placeholder={t("order.multiPayment.paymentTimesLabel")}
                      slotProps={{
                        button: { sx: { whiteSpace: "nowrap" } },
                      }}
                      onChange={(_event, newValue) => {
                        setValue("multiPaymentsData.paymentTimes", newValue);
                        setValue(
                          "multiPaymentsData.paymentHistory",
                          Array.from({ length: newValue }).map((_, num) => {
                            return { time: num + 1, percentage: 0 };
                          })
                        );
                      }}
                      value={value}
                      className="mt-auto h-[41.6px]"
                    >
                      {Array.from({ length: 13 }).map(
                        (_, num) =>
                          num > 1 && (
                            <Option key={num} value={num} id="payment-opt">
                              {num} Payments
                            </Option>
                          )
                      )}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
          )}
        </Grid>
        <Grid xs={12} lg={6}>
          <Controller
            name="currency"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, ref } }) => (
              <FormControl sx={{ flex: "1", height: "100%", flexGrow: 1 }}>
                <FormLabel>
                  {t("catalog.inventory.editForm.currency")}
                  <span className="text-danger-400">*</span>
                </FormLabel>

                <Select
                  ref={ref}
                  color={errors.currency ? "danger" : "neutral"}
                  indicator={<KeyboardArrowDown />}
                  placeholder={t("catalog.inventory.editForm.currency")}
                  slotProps={{
                    button: {
                      sx: { whiteSpace: "nowrap" },
                    },
                  }}
                  onChange={(_event, newValue: string | null) => {
                    if (
                      !newValue ||
                      // !oldCurrency ||
                      oldCurrency === newValue
                    ) {
                      return;
                    }

                    setCurrencyModal({
                      show: true,
                      currency: newValue,
                    });
                  }}
                  value={value}
                  className="mt-auto h-[41.6px]"
                  id="create-catalog-currency-autocomplete"
                >
                  {currencyList.map((item) => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </Select>

                <FormHelperText>
                  {t("catalog.createCatalog.currencyNote")}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid xs={12} lg={6}>
          <Controller
            name={`taxId`}
            control={control}
            render={({
              field: { onChange, onBlur, name, ref },
              fieldState: { error },
            }) => (
              <FormControl sx={{ flex: "1" }} error={!!error} ref={ref}>
                <FormLabel>
                  {t("order.createOrder.productForm.taxAdditional")}
                </FormLabel>
                <Autocomplete
                  name={name}
                  placeholder={t("order.createOrder.productForm.taxAdditional")}
                  popupIcon={<KeyboardArrowDown />}
                  options={nationalTaxes || []}
                  getOptionLabel={(option) => option?.taxLabel || ""}
                  onChange={(_event, newValue: any) => {
                    onChange(newValue);
                    console.log("National Tax Item changed? ::", newValue);
                    if (newValue) {
                      setValue("taxId", newValue.id);
                    }
                  }}
                  onBlur={onBlur}
                  value={
                    selectedTax ||
                    catalog.taxId ||
                    nationalTaxes.find((tax) => tax.taxLabel === "NONE") ||
                    null
                  }
                  id="create-catalog-taxAdditional-autocomplete"
                />

                <FormHelperText>{error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
        <Grid xs={12} lg={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="periodStartDate"
              control={control}
              render={({
                field: { name, value, ref, onChange },
                fieldState: { error },
              }) => (
                <FormControl sx={{ flexGrow: 1 }}>
                  <FormLabel>
                    {t("catalog.createCatalog.catalogStartPeriod")}
                  </FormLabel>
                  <DesktopDatePicker
                    views={["year", "month", "day"]}
                    format="YYYY/MM/DD"
                    minDate={dayjs(new Date()).utc(true)}
                    slotProps={{
                      textField: {
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderRadius: "8px",
                            },
                          },
                        },
                      },
                      popper: {
                        sx: {
                          "& .MuiDateCalendar-root": {
                            height: "auto !important",
                          },
                        },
                      },
                      yearButton: {
                        sx: {
                          width: "100%",
                          height: "100%",
                          borderRadius: 0,
                          border: "1px solid var(--joy-palette-neutral-200)",
                          "&:hover, &:focus, &.Mui-selected": {
                            backgroundColor:
                              "var(--joy-palette-primary-500) !important",
                            color: "var(--joy-palette-common-white)",
                          },
                          "&.Mui-disabled": {
                            background: "var(--joy-palette-neutral-100)",
                            color: "var(--joy-palette-neutral-400)",
                          },
                        },
                      },
                      monthButton: {
                        sx: {
                          width: "100%",
                          height: "100%",
                          borderRadius: 0,
                          border: "1px solid var(--joy-palette-neutral-200)",
                          "&:hover, &:focus, &.Mui-selected": {
                            backgroundColor:
                              "var(--joy-palette-primary-500) !important",
                            color: "var(--joy-palette-common-white)",
                          },
                          "&.Mui-disabled": {
                            background: "var(--joy-palette-neutral-100)",
                            color: "var(--joy-palette-neutral-400)",
                          },
                        },
                      },
                    }}
                    name={name}
                    value={value ? dayjs(value, "YYYY-MM-DD") : null}
                    onChange={(date) => {
                      const d = dayjs(date).format("YYYY-MM-DD");
                      onChange(dayjs(d).isValid() ? d : null);
                    }}
                    inputRef={ref}
                  />
                  {error?.message}
                </FormControl>
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid xs={12} lg={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
              name="periodEndDate"
              control={control}
              render={({
                field: { name, value, ref, onChange },
                fieldState: { error },
              }) => (
                <FormControl sx={{ flexGrow: 1 }}>
                  <FormLabel>
                    {t("catalog.createCatalog.catalogEndPeriod")}
                  </FormLabel>
                  <DesktopDatePicker
                    views={["year", "month", "day"]}
                    format="YYYY/MM/DD"
                    minDate={dayjs(new Date()).utc(true)}
                    slotProps={{
                      textField: {
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderRadius: "8px",
                            },
                          },
                        },
                      },
                      popper: {
                        sx: {
                          "& .MuiDateCalendar-root": {
                            height: "auto !important",
                          },
                        },
                      },
                      yearButton: {
                        sx: {
                          width: "100%",
                          height: "100%",
                          borderRadius: 0,
                          border: "1px solid var(--joy-palette-neutral-200)",
                          "&:hover, &:focus, &.Mui-selected": {
                            backgroundColor:
                              "var(--joy-palette-primary-500) !important",
                            color: "var(--joy-palette-common-white)",
                          },
                          "&.Mui-disabled": {
                            background: "var(--joy-palette-neutral-100)",
                            color: "var(--joy-palette-neutral-400)",
                          },
                        },
                      },
                      monthButton: {
                        sx: {
                          width: "100%",
                          height: "100%",
                          borderRadius: 0,
                          border: "1px solid var(--joy-palette-neutral-200)",
                          "&:hover, &:focus, &.Mui-selected": {
                            backgroundColor:
                              "var(--joy-palette-primary-500) !important",
                            color: "var(--joy-palette-common-white)",
                          },
                          "&.Mui-disabled": {
                            background: "var(--joy-palette-neutral-100)",
                            color: "var(--joy-palette-neutral-400)",
                          },
                        },
                      },
                    }}
                    name={name}
                    value={value ? dayjs(value, "YYYY-MM-DD") : null}
                    onChange={(date) => {
                      const d = dayjs(date).format("YYYY-MM-DD");
                      onChange(dayjs(d).isValid() ? d : null);
                    }}
                    inputRef={ref}
                  />
                  {error?.message}
                </FormControl>
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid xs={12} lg={6}>
          <FormControl sx={{ flexGrow: 1 }}>
            <FormLabel>
              {t("catalog.createCatalog.defaultCatalogView")}
            </FormLabel>
            <Typography level="text-xs-regular">
              {t("catalog.createCatalog.defaultCatalogViewSubtitle")}{" "}
              <Sheet
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Tooltip
                  title={
                    <Stack>
                      <Typography level="text-xs-semibold">
                        <p>Tip: Buyers will see your chosen view first</p>
                        <p>but can switch using the icon below.</p>
                      </Typography>
                      <Grid container mt={"8px"}>
                        <Grid xs={6}>
                          <Stack direction={"column"} alignItems={"center"}>
                            <Stack
                              direction={"row"}
                              className="border border-gray-400 rounded-lg overflow-hidden max-w-[78px]"
                              justifyContent={"flex-end"}
                              alignItems={"center"}
                            >
                              <IconButton
                                variant="plain"
                                className={`h-[100%] rounded-none px-2 bg-primary-200`}
                              >
                                <ListBulletIcon className="w-6" />
                              </IconButton>
                              <span className="border-l border-gray-400 h-[100%]"></span>
                              <IconButton
                                variant="plain"
                                className={`h-[100%] rounded-none`}
                              >
                                <Squares2X2Icon className="w-6" />
                              </IconButton>
                            </Stack>
                            <Typography>List</Typography>
                          </Stack>
                        </Grid>
                        <Grid xs={6}>
                          <Stack direction={"column"} alignItems={"center"}>
                            <Stack
                              direction={"row"}
                              className="border border-gray-400 rounded-lg overflow-hidden max-w-[78px]"
                              justifyContent={"flex-end"}
                              alignItems={"center"}
                            >
                              <IconButton
                                variant="plain"
                                className={`h-[100%] rounded-none`}
                              >
                                <ListBulletIcon className="w-6" />
                              </IconButton>
                              <span className="border-l border-gray-400 h-[100%]"></span>
                              <IconButton
                                variant="plain"
                                className={`h-[100%] rounded-none px-2 bg-primary-200`}
                              >
                                <Squares2X2Icon className="w-6" />
                              </IconButton>
                            </Stack>
                            <Typography>Grid</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Stack>
                  }
                  arrow
                  placement="bottom"
                  variant="soft"
                >
                  <span style={{ position: "relative", top: "4px" }}>
                    <InfoIcon width={18} height={18} />
                  </span>
                </Tooltip>
              </Sheet>
            </Typography>
            <Box
              sx={{
                border: `1px solid ${theme.palette.neutral[300]}`,
                borderRadius: "12px",
                p: "16px",
              }}
              mt={2}
            >
              <RadioGroup
                sx={{ display: "flex", gap: 1 }}
                defaultValue={CatalogLayout.LIST}
                name="layoutItems"
                onChange={(_event) => {
                  setValue("layoutItems", _event.target.value);
                }}
                value={watch("layoutItems")}
              >
                <Radio
                  sx={{ padding: 1 }}
                  value={CatalogLayout.LIST}
                  label={t("catalog.createCatalog.listView")}
                />
                <Radio
                  sx={{ padding: 1 }}
                  value={CatalogLayout.GRID}
                  label={t("catalog.createCatalog.gridView")}
                />
              </RadioGroup>
            </Box>
          </FormControl>
        </Grid>
        <Grid xs={12} lg={6}>
          <FormControl sx={{ flexGrow: 1 }}>
            <FormLabel>{t("catalog.createCatalog.showProductStock")}</FormLabel>
            <Typography level="text-xs-regular">
              {t("catalog.createCatalog.showProductStockDesc")}
            </Typography>
            <Box
              sx={{
                border: `1px solid ${theme.palette.neutral[300]}`,
                borderRadius: "12px",
                p: "16px",
              }}
              mt={2}
            >
              <RadioGroup
                sx={{ display: "flex", gap: 1 }}
                defaultValue={true}
                name="hideProductStock"
                onChange={(_event) => {
                  setValue("hideProductStock", _event.target.value === "true");
                }}
                value={watch("hideProductStock")}
              >
                <Radio
                  sx={{ padding: 1 }}
                  value={true}
                  label={t("catalog.createCatalog.hideProductStock")}
                />
                <Radio
                  sx={{ padding: 1 }}
                  value={false}
                  label={t("catalog.createCatalog.showProductStock")}
                />
              </RadioGroup>
            </Box>
          </FormControl>
        </Grid>
      </Grid>

      {/* Payment Times Loop */}
      {watch("paymentFlowType") === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS &&
        (watch("multiPaymentsData.paymentTimes") ?? 0) > 1 && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.neutral[300]}`,
              borderRadius: "12px",
              p: "16px",
            }}
            mt={2}
          >
            <Toggler
              defaultExpanded
              renderToggle={({ open, setOpen, sxClasses }) => (
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  onClick={() => setOpen(!open)}
                  sx={{
                    ...sxClasses,
                  }}
                >
                  <Typography level="text-md-semibold">
                    {t("order.multiPayment.paymentPlan")}
                    <span className="text-danger-400">*</span>
                  </Typography>

                  <IconButton variant="plain">
                    {open ? (
                      <ChevronUpIcon width={20} height={20} />
                    ) : (
                      <ChevronDownIcon width={20} height={20} />
                    )}
                  </IconButton>
                </Stack>
              )}
            >
              <Grid
                container
                sx={{
                  gap: "16px",
                }}
              >
                <ThreeColumnGridRow
                  label={t("order.multiPayment.paymentValueType.label")}
                  inputElement={
                    <Select
                      disabled={true}
                      value={"PERCENTAGE"}
                      defaultValue={"AMOUNT"}
                      indicator={<KeyboardArrowDown />}
                      sx={{ width: 260 }}
                      id="payment-plan-select"
                    >
                      <Option id="payment-opt-percentage" value="PERCENTAGE">
                        Percentage
                      </Option>
                    </Select>
                  }
                  outputValue={""}
                />

                {(watch("multiPaymentsData.paymentHistory") ?? []).map(
                  (payment: any) => (
                    <ThreeColumnGridRow
                      key={payment.time}
                      label={`${t("commons.payment")} ${payment.time}`}
                      inputElement={
                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                          <Controller
                            name={`multiPaymentsData.paymentHistory.${
                              payment.time - 1
                            }.percentage`}
                            control={control}
                            render={({
                              field: { onChange, name, value },
                            }: any) => (
                              <Stack>
                                <NumericFormat
                                  name={name}
                                  value={value}
                                  customInput={Input}
                                  decimalScale={2}
                                  fixedDecimalScale={true}
                                  sx={{ maxWidth: 260 }}
                                  placeholder={""}
                                  endDecorator={"%"}
                                  {...{
                                    isAllowed: (values) => {
                                      const { floatValue } = values;
                                      return (
                                        floatValue === undefined ||
                                        floatValue <= 100
                                      ); // Limit input to 100%
                                    },
                                  }}
                                  onValueChange={(v) => {
                                    const value = v.value;
                                    onChange(Number(value) || 0);
                                    if (`${parseFloat(value)}` !== "NaN") {
                                      setValue(
                                        `multiPaymentsData.paymentHistory.${
                                          payment.time - 1
                                        }.percentage`,
                                        parseFloat(value)
                                      );
                                    }
                                  }}
                                />
                                {(errors as any)?.multiPaymentsData
                                  ?.paymentHistory?.[payment.time - 1]
                                  ?.percentage?.message && (
                                  <FormHelperText
                                    color="danger"
                                    style={{ marginBottom: "2px" }}
                                  >
                                    {
                                      (errors as any)?.multiPaymentsData
                                        ?.paymentHistory?.[payment.time - 1]
                                        ?.percentage?.message
                                    }
                                  </FormHelperText>
                                )}
                              </Stack>
                            )}
                          />
                        </Stack>
                      }
                    />
                  )
                )}

                {(watch("multiPaymentsData.paymentHistory") as any[])?.reduce(
                  (acc, curr) => acc + curr.percentage,
                  0
                ) !== 100 && (
                  <Typography color="danger" fontSize={"sm"}>
                    {t("order.multiPayment.errors.percentagePrompt")}
                  </Typography>
                )}
              </Grid>
            </Toggler>
          </Box>
        )}

      {/* modal */}
      <ChangeCurrencyModal
        show={currencyModal.show}
        onClick={() => {
          setValue("currency", currencyModal.currency);
          setOldCurrency(currencyModal.currency);
          setCurrencyModal({
            show: false,
            currency: currencyModal.currency,
          });
        }}
        onClose={() => {
          setValue("currency", oldCurrency ?? null);
          console.log("OLD CURRENCY", oldCurrency);
          setCurrencyModal({
            show: false,
            currency: oldCurrency,
          });
        }}
      />
    </Box>
  );
};

export default CatalogInfoSection;
